import { IColumn } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPLabel, IPText } from 'baseComponents';
import { IPDetailsList, IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import { useEntityAudits } from 'components/Audit/useEntityAudits';
import HrefLink from 'components/Links/HrefLink';
import EntityPicker from 'components/Pickers/EntityPicker';
import { getEntity, getEntityName, onRenderDetailsHeaderGlobal } from 'globalFunctions';
import { deleteIcon, globalStackTokensGapMedium } from 'globalStyles';
import { TFunction } from 'i18next';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import Control from 'models/control';
import Entity, { EntityTypes } from 'models/entity';
import Process from 'models/process/process';
import ProcessingActivity from 'models/processingActivity';
import Risk from 'models/risk';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetControls } from 'services/Api/controlService';
import {
  apiAddControlToProcessingActivity,
  apiAddProcessToProcessingActivity,
  apiAddRiskToProcessingActivity,
  apiGetProcessingActivityContext,
  apiRemoveControlFromProcessingActivity,
  apiRemoveProcessFromProcessingActivity,
  apiRemoveRiskFromProcessingActivity,
} from 'services/Api/processingActivityService';
import { apiGetProcesses } from 'services/Api/processService';
import { apiGetRisks } from 'services/Api/riskService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserDataPermission } from 'services/Auth/featurePermissions';
import { sortOnEntity } from 'utils/sorting';

interface IProcessingActivityContextProps {
  processingActivity?: ProcessingActivity;
}

export const ProcessingActivityContext = (props: IProcessingActivityContextProps) => {
  const { t } = useTranslation(['processingActivity']);
  const appContext = useContext(AppContext);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [allEntities, setAllEntities] = useState<Entity[]>([]);
  const [allProcesses, setAllProcesses] = useState<Process[]>([]);
  const [allControls, setAllControls] = useState<Control[]>([]);
  const [allRisks, setAllRisks] = useState<Risk[]>([]);
  const [isBusyAddingRemoving, setIsBusyAddingRemoving] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingPicker, setIsLoadingPicker] = useState<boolean>(false);

  const [canUpdate] = useState<boolean>(
    props.processingActivity
      ? hasUserDataPermission(appContext, [props.processingActivity.authSchemaId], AuthSchemaLineOperation.Update)
      : false,
  );

  //the audits that cover this processing activity are relations of it as well, shown in the same list
  const auditEntities = useEntityAudits(EntityTypes.ProcessingActivity, props.processingActivity?.processingActivityId);
  const relatedEntities = [...entities, ...auditEntities].sort((a, b) => sortOnEntity(a, b));

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.processingActivity]);

  const loadData = async () => {
    if (isLoading || !props.processingActivity) return;

    try {
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiGetProcessingActivityContext(
        props.processingActivity.processingActivityId,
        accessToken,
        appContext,
      );

      const entities: Entity[] = [];

      result.processes.forEach((process) => {
        entities.push(getEntity(process));
      });

      result.controls.forEach((control) => {
        entities.push(getEntity(control));
      });

      result.risks.forEach((risk) => {
        entities.push(getEntity(risk));
      });

      setEntities(entities.sort((a, b) => sortOnEntity(a, b)));
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDataForPicker = async () => {
    if (isLoadingPicker) return;

    try {
      setIsLoadingPicker(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      const _allProcessesPromise = apiGetProcesses(accessToken, appContext.globalDataCache);
      const _allControlsPromise = apiGetControls(accessToken, appContext.globalDataCache);
      const _allRisksPromise = apiGetRisks(accessToken, appContext);

      const [_allControlsData, _allProcessesData, _allRisksData] = await Promise.allSettled([
        _allControlsPromise,
        _allProcessesPromise,
        _allRisksPromise,
      ]);

      const _allEntities: Entity[] = [];
      let errorMessage = '';

      if (_allControlsData.status === 'fulfilled') {
        _allControlsData.value.forEach((_control: Control) => {
          _allEntities.push(getEntity(_control));
        });
        setAllControls(_allControlsData.value);
      } else {
        errorMessage += `${t('control:Title')} : ${_allControlsData.reason}`;
      }

      if (_allRisksData.status === 'fulfilled') {
        _allRisksData.value.forEach((_risk: Risk) => {
          _allEntities.push(getEntity(_risk));
        });
        setAllRisks(_allRisksData.value.risks);
      } else {
        errorMessage += `${t('risk:Title')} : ${_allRisksData.reason}`;
      }

      if (_allProcessesData.status === 'fulfilled') {
        _allProcessesData.value.forEach((_process: Process) => {
          _allEntities.push(getEntity(_process));
        });
        setAllProcesses(_allProcessesData.value);
      } else {
        errorMessage += `${t('process:Title')} : ${_allProcessesData.reason}`;
      }

      if (errorMessage) appContext.setError(errorMessage);

      setAllEntities(_allEntities);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoadingPicker(false);
    }
  };

  const columns: IColumn[] = [
    {
      key: 'type',
      name: t('processingActivity:Context.List.Type'),
      minWidth: 80,
      maxWidth: 80,
      onRender: (item?: Entity, index?: number, column?: IColumn) => {
        if (!item) return;

        return <IPText>{getEntityName(item, t as TFunction<string[]>)}</IPText>;
      },
    },
    {
      key: 'name',
      name: t('processingActivity:Context.List.Name'),
      isMultiline: true,
      isResizable: true,
      minWidth: 100,
      onRender: (item?: Entity, index?: number, column?: IColumn) => {
        if (!item) return;

        return (
          <IPText>
            <HrefLink entity={item}>{item.entityName}</HrefLink>
          </IPText>
        );
      },
    },
    {
      key: 'remove',
      name: t('processingActivity:Context.List.Remove'),
      minWidth: 100,
      maxWidth: 100,
      onRender: (item?: Entity, index?: number, column?: IColumn) => {
        //an audit is no relation the user maintains here: its scope is decided in the audit itself
        if (!canUpdate || item?.typeOfEntity === EntityTypes.InternalAudit) return null;

        return (
          <IPIconButton
            iconProps={deleteIcon}
            onClick={() => {
              if (!item) return;

              onRemove(item);
            }}
            styles={{ root: { height: 18 } }}
          />
        );
      },
    },
  ];

  const filterEntities = (all: Entity[], related: Entity[]): Entity[] => {
    return all.filter((e) => {
      const foundControl = related.find((r) => {
        return r.entityId === e.entityId && r.typeOfEntity === e.typeOfEntity;
      });
      if (foundControl) return false;

      return true;
    });
  };

  const addEntity = async (entity: Entity) => {
    if (isBusyAddingRemoving || !props.processingActivity) return false;
    try {
      setIsBusyAddingRemoving(entity.entityId);
      let validAdd = false;
      switch (entity.typeOfEntity) {
        case EntityTypes.Control: {
          validAdd = allControls.find((c) => c.controlId === entity.entityId) !== undefined;
          break;
        }
        case EntityTypes.Risk: {
          validAdd = allRisks.find((c) => c.riskId === entity.entityId) !== undefined;
          break;
        }
        case EntityTypes.Process: {
          validAdd = allProcesses.find((c) => c.processId === entity.entityId) !== undefined;
          break;
        }
        default:
          break;
      }

      if (validAdd) {
        const added = await addProcessingActivityToEntity(
          props.processingActivity.processingActivityId,
          entity.entityId,
          entity.typeOfEntity,
        );
        if (added) setEntities([...entities, entity]);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsBusyAddingRemoving(0);
    }
  };

  const addProcessingActivityToEntity = async (
    processingActivityId: number,
    toId: number,
    toType: EntityTypes,
  ): Promise<boolean> => {
    if (isLoading || !props.processingActivity) return false;

    let output = false;
    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      switch (toType) {
        case EntityTypes.Process: {
          output = await apiAddProcessToProcessingActivity(processingActivityId, toId, accessToken);
          break;
        }
        case EntityTypes.Control: {
          output = await apiAddControlToProcessingActivity(processingActivityId, toId, accessToken);
          break;
        }
        case EntityTypes.Risk: {
          output = await apiAddRiskToProcessingActivity(processingActivityId, toId, accessToken);
          break;
        }
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }

    return output;
  };

  const removeProcessingActivityFromEntity = async (
    processingActivityId: number,
    entityId: number,
    toType: EntityTypes,
  ) => {
    if (isLoading || !props.processingActivity) return false;

    let output = false;
    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      switch (toType) {
        case EntityTypes.Process: {
          output = await apiRemoveProcessFromProcessingActivity(processingActivityId, entityId, accessToken);
          break;
        }
        case EntityTypes.Control: {
          output = await apiRemoveControlFromProcessingActivity(processingActivityId, entityId, accessToken);
          break;
        }
        case EntityTypes.Risk: {
          output = await apiRemoveRiskFromProcessingActivity(processingActivityId, entityId, accessToken);
          break;
        }
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }

    return output;
  };

  const onRemove = async (entity: Entity) => {
    try {
      if (isBusyAddingRemoving || !props.processingActivity) return;
      setIsBusyAddingRemoving(entity.entityId);

      const removed = await removeProcessingActivityFromEntity(
        props.processingActivity?.processingActivityId,
        entity.entityId,
        entity.typeOfEntity,
      );
      if (removed) {
        setEntities(entities.filter((c) => c.typeOfEntity !== entity.typeOfEntity || c.entityId !== entity.entityId));
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsBusyAddingRemoving(0);
    }
  };

  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPLabel> {t('processingActivity:Context.Relations')}</IPLabel>
        <IPDetailsList
          compact
          items={relatedEntities}
          columns={columns}
          selectionMode={IPSelectionMode.none}
          layoutMode={IPDetailsListLayoutMode.justified}
          onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
        />
      </IPStack.Item>
      {canUpdate && (
        <IPStack.Item grow>
          <IPStack verticalFill>
            <IPStack.Item>
              <IPLabel>{t('processingActivity:Context.AddText')}</IPLabel>
            </IPStack.Item>
            <IPStack.Item grow>
              <EntityPicker
                entities={filterEntities(allEntities, entities)}
                addSelectedEntity={addEntity}
                loadData={loadDataForPicker}
                isLoading={isLoadingPicker}
                isOnPanel={appContext.isMobileView}
                isBusyAdding={isBusyAddingRemoving}
              />
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      )}
    </IPStack>
  );
};
